import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Table, TableBody, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import ReactECharts from 'echarts-for-react';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },

    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const Dashboard = ({ npsScore }) => {
    const [tableData, setTableData] = useState([]);
    const [quaterlyResponses, setQuaterlyResponses] = useState([]);

    const totalPromoters = npsScore?.length
        ? npsScore.reduce((sum, survey) => sum + survey.promoters, 0)
        : 0;

    const totalPassives = npsScore?.length
        ? npsScore.reduce((sum, survey) => sum + survey.passives, 0)
        : 0;

    const totalDetractors = npsScore?.length
        ? npsScore.reduce((sum, survey) => sum + survey.detractors, 0)
        : 0;

    const totalNpsScores = npsScore?.length
        ? npsScore.reduce((sum, survey) => sum + survey.npsScore, 0)
        : 0;

    const averageNpsScore = npsScore?.length
        ? (totalNpsScores / npsScore.length).toFixed(2)
        : 0;

    const npsScoreTrimmed = npsScore?.length > 12
        ? npsScore.slice(npsScore.length - 12)
        : (npsScore || []);

    const newNpsScoreData = npsScoreTrimmed.map(score => ({
        quarter: score.name?.split(' ').slice(0, 2).join('-') ?? 'Unknown',
        value: score.npsScore ?? 0
    })).reverse();

    const xAxisQuarters = newNpsScoreData.map(q => q.quarter);

    const totalResponsesData = xAxisQuarters.map(q => {
        const match = quaterlyResponses.find(r => r.quarter === q);
        return match ? match.totalResponses : 0;
    });

    const gaugeOption = {
        series: [
            {
                type: 'gauge',
                startAngle: 225,
                endAngle: -45,
                min: -100,
                max: 100,
                splitNumber: 10,
                radius: '97%',
                axisLine: {
                    lineStyle: {
                        width: 25,
                        color: [
                            [0.4, '#d9534f'],
                            [0.7, '#f0ad4e'],
                            [1, '#5cb85c']
                        ]
                    }
                },
                pointer: {
                    width: 10,
                    length: '75%'
                },
                detail: {
                    formatter: '{value}',
                    fontSize: 27,
                    fontWeight: 'bold',
                    offsetCenter: [0, '70%']
                },
                splitLine: {
                    length: 17
                },
                axisLabel: {
                    distance: 30,
                    fontSize: 20
                },
                data: [{ value: averageNpsScore }]
            }
        ]
    };

    const lineChartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross'
            },
            formatter: (params) => {
                return params.map(p => `${p.seriesName}: ${p.value}`).join('<br/>');
            }
        },
        legend: {
            data: ['NPS Score', 'Total Responses']
        },
        xAxis: {
            type: 'category',
            data: newNpsScoreData.map(q => q.quarter),
            axisLabel: {
                interval: 0
            }
        },
        yAxis: [
            {
                type: 'value',
                name: 'NPS Score',
                position: 'left',
                axisLine: {
                    show: true
                }
            },
            {
                type: 'value',
                name: 'Total Responses',
                position: 'right',
                axisLine: {
                    show: true
                }
            }
        ],
        series: [
            {
                name: 'NPS Score',
                type: 'line',
                yAxisIndex: 0,
                data: newNpsScoreData.map(d => d.value),
                smooth: true,
                lineStyle: { width: 3 },
                symbolSize: 8
            },
            {
                name: 'Total Responses',
                type: 'bar',
                yAxisIndex: 1,
                data: totalResponsesData,
                barWidth: '50%',
                itemStyle: {
                    color: 'rgba(31, 189, 74, 0.7)'
                }
            }
        ]
    };

    useEffect(() => {
        if (!npsScore || npsScore.length === 0) return;

        const aggregatedData = Array.from({ length: 11 }, (_, score) => ({
            score,
            detractors: 0,
            passives: 0,
            promoters: 0
        }));

        const aggregatedResponses = {};

        npsScore.forEach(survey => {
            const key = survey.name.split(' ').slice(0, 2).join('-');

            if (!aggregatedResponses[key]) {
                aggregatedResponses[key] = 0;
            }

            aggregatedResponses[key] += survey.totalResponses;

            for (let score = 0; score <= 10; score++) {
                const key = `score_${score}`;
                const count = survey[key] ?? 0;

                if (score <= 6) {
                    aggregatedData[score].detractors += count;
                }
                else if (score >= 7 && score <= 8) {
                    aggregatedData[score].passives += count;
                }
                else if (score >= 9) {
                    aggregatedData[score].promoters += count;
                }
            }
        });

        const formattedData = aggregatedData.map(entry => {
            return {
                score: entry.score,
                ...(entry.detractors ? { detractors: entry.detractors } : {}),
                ...(entry.passives ? { passives: entry.passives } : {}),
                ...(entry.promoters ? { promoters: entry.promoters } : {})
            };
        });

        const formattedResponses = Object.entries(aggregatedResponses).map(([key, totalResponses]) => ({
            quarter: key,
            totalResponses
        }));

        setTableData(formattedData);
        setQuaterlyResponses(formattedResponses);
    }, [npsScore]);

    return (
        <Grid container spacing={1}>
            <Grid size={{ xs: 12, lg: 5 }}>
                <Card sx={{ boxShadow: 3, height: '100%' }}>
                    <CardContent>
                        <Typography variant="h5" align="center" gutterBottom>
                            NPS Score
                        </Typography>
                        <ReactECharts option={gaugeOption} style={{ height: 450 }} />

                        <Grid container spacing={2} sx={{ justifyContent: 'center', mt: 1 }}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Card sx={{ textAlign: 'center' }} elevation={3}>
                                    <CardHeader
                                        sx={{ p: 1 }}
                                        title="Detractors (0-6)"
                                        slotProps={{
                                            title: {
                                                variant: 'h6'
                                            }
                                        }}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Typography variant="h4">
                                            {totalDetractors}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Card sx={{ textAlign: 'center' }} elevation={3}>
                                    <CardHeader
                                        sx={{ p: 1 }}
                                        title="Passives (7-8)"
                                        slotProps={{
                                            title: {
                                                variant: 'h6'
                                            }
                                        }}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Typography variant="h4">
                                            {totalPassives}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Card sx={{ textAlign: 'center' }} elevation={3}>
                                    <CardHeader
                                        sx={{ p: 1 }}
                                        title="Promoters (9-10)"
                                        slotProps={{
                                            title: {
                                                variant: 'h6'
                                            }
                                        }}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Typography variant="h4">
                                            {totalPromoters}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 7 }} direction="column" spacing={2}>
                <Grid size="auto">
                    <TableContainer component={Paper} elevation={3}>
                        <Table sx={{ minWidth: 700 }} size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Score</StyledTableCell>
                                    <StyledTableCell align="center">Detractors</StyledTableCell>
                                    <StyledTableCell align="center">Passives</StyledTableCell>
                                    <StyledTableCell align="center">Promoters</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.map((row, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell component="th" scope="row">
                                            {row.score}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{row.detractors}</StyledTableCell>
                                        <StyledTableCell align="center">{row.passives}</StyledTableCell>
                                        <StyledTableCell align="center">{row.promoters}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid size="auto" sx={{ mt: 2 }}>
                    <Card sx={{ boxShadow: 3 }}>
                        <CardContent sx={{ px: 0 }}>
                            <Typography variant="h5" align="center" gutterBottom>
                                Average of NPS Score by Period
                            </Typography>
                            <ReactECharts option={lineChartOption} style={{ height: 270 }} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Dashboard;
